"use client";

import { type ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { getServerI18n, initI18n } from '@/lib/i18n';
import { DEFAULT_NAMESPACE } from '@/lib/i18n/resources';

type I18nProviderProps = {
    children: ReactNode;
    defaultLocale?: string;
};

export const I18nProvider = ({ children, defaultLocale }: I18nProviderProps) => {
    // SSR：用「按 locale 缓存的只读实例」渲染，避免模块级单例在并发请求间串语言。
    // 客户端：初始化并复用全局单例（可 changeLanguage、可被 i18next.t 等直接引用）。
    // 两端都用同一份内联资源 + 同一 defaultLocale，首帧文本一致，无闪烁、无 hydration mismatch。
    const instance =
        typeof window === 'undefined' ? getServerI18n(defaultLocale) : initI18n(defaultLocale);

    return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
};

// 可选 namespace：Studio 传 'studio' 以使用其专属命名空间（其它语言仅本地化了 Studio）。
// 传入非默认命名空间时，自动以 [ns, 'translation'] 数组形式查找：先查该命名空间，未命中再回退
// `translation`（如模型权限/通用文案）。这是「按需」回退，不会像全局 fallbackNS 那样影响 market 等。
// 不传则沿用默认 `translation` 命名空间，行为与之前一致。
export const useI18n = (ns?: string) =>
  useTranslation(ns && ns !== DEFAULT_NAMESPACE ? [ns, DEFAULT_NAMESPACE] : ns);

export default I18nProvider;
